import fetcher from '../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { unwrapDataResponse } from '../../utils/unwrapDataResponse';
import { indexedDBManager } from '../../utils/indexedDB';
import type {
  CreatePresentationDto,
  FetchProductPresentationDto,
  FetchProductPresentationPriceRecordsDto,
  LatestPricesResponse,
  PaginatedResponse,
  Presentation,
  PresentationQueryParams,
  PresentationResponse,
  ProductByMainProductItem,
  ProductPresentationItem,
  ProductPresentationPriceRecord,
  PublicPresentationResponse,
  UpdatePresentationDto,
} from '../../types/presentation';

class ProductService {
  private consumerPriceUrl = ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE;
  private analyticsUrl = ENVIRONMENT_CONFIG.ANALYSIS_SERVICE;
  private entity = 'presentation';

  async getProducts(params: PresentationQueryParams = {}): Promise<PresentationResponse> {
    const queryParams: Record<string, string> = {};

    if (params.page) queryParams.page = params.page.toString();
    if (params.limit) queryParams.limit = params.limit.toString();
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;
    if (params.mainCategory) queryParams.mainCategory = params.mainCategory;
    if (params.category) queryParams.category = params.category;
    if (params.subCategory) queryParams.subCategory = params.subCategory;
    if (params.basketId) queryParams.basketId = params.basketId.toString();
    if (params.search) queryParams.search = params.search;
    if (params.since) queryParams.since = params.since.toString();
    if (params.status) queryParams.status = params.status;

    const response = await fetcher.get(
      API_ENDPOINTS.PRESENTATION.GET_PRESENTATION_LIST,
      queryParams,
      undefined,
      this.consumerPriceUrl
    );
    return unwrapDataResponse<PresentationResponse>(response.data);
  }

  async getPublicProducts(
    params: PresentationQueryParams = {}
  ): Promise<PublicPresentationResponse> {
    const queryParams: Record<string, string> = {};

    if (params.page) queryParams.page = params.page.toString();
    if (params.limit) queryParams.limit = params.limit.toString();
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;
    if (params.mainCategory) queryParams.mainCategory = params.mainCategory;
    if (params.category) queryParams.category = params.category;
    if (params.subCategory) queryParams.subCategory = params.subCategory;
    if (params.basketId) queryParams.basketId = params.basketId.toString();
    if (params.search) queryParams.search = params.search;
    if (params.since) queryParams.since = params.since.toString();
    if (params.locationId) queryParams.locationId = params.locationId.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.PRESENTATION.GET_PUBLIC_PRESENTATIONS,
      queryParams,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as PublicPresentationResponse;
  }

  async getProductById(id: number): Promise<Presentation> {
    const response = await fetcher.get(
      `${API_ENDPOINTS.PRESENTATION.PRESENTATIONS}/${id}`,
      undefined,
      undefined,
      this.analyticsUrl
    );
    return response.data as unknown as Presentation;
  }

  async getProductByMainProductId(
    mainProductId: number | string
  ): Promise<ProductByMainProductItem[]> {
    const response = await fetcher.get(
      `${API_ENDPOINTS.PRESENTATION.GET_PRESENTATIONS_BY_MAIN_PRODUCT}/${mainProductId}`,
      undefined,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as ProductByMainProductItem[];
  }

  async getProductPresentations(
    data: FetchProductPresentationDto
  ): Promise<PaginatedResponse<ProductPresentationItem>> {
    const response = await fetcher.post(
      API_ENDPOINTS.PRESENTATION.GET_PRODUCT_PRESENTATIONS,
      data,
      undefined,
      this.analyticsUrl
    );
    return response.data as PaginatedResponse<ProductPresentationItem>;
  }

  async getProductPresentationPriceRecords(
    data: FetchProductPresentationPriceRecordsDto
  ): Promise<PaginatedResponse<ProductPresentationPriceRecord>> {
    const response = await fetcher.post(
      API_ENDPOINTS.PRESENTATION.GET_PRODUCT_PRESENTATION_PRICE_RECORDS,
      data,
      undefined,
      this.analyticsUrl
    );
    return response.data as PaginatedResponse<ProductPresentationPriceRecord>;
  }

  async createProduct(product: CreatePresentationDto): Promise<Presentation> {
    const response = await fetcher.post(
      API_ENDPOINTS.PRESENTATION.PRESENTATIONS,
      product,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as Presentation;
  }

  async updateProduct(id: number, product: UpdatePresentationDto): Promise<Presentation> {
    const response = await fetcher.patch(
      `${API_ENDPOINTS.PRESENTATION.PRESENTATIONS}/${id}`,
      product,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as Presentation;
  }

  async deleteProduct(id: number, refId?: string): Promise<void> {
    await fetcher.patch(
      `${API_ENDPOINTS.PRESENTATION.PRESENTATIONS}/${id}/remove`,
      {},
      undefined,
      this.consumerPriceUrl
    );

    if (refId) {
      await indexedDBManager.updateLocalData(this.entity, refId, {
        isActive: false,
        updateTime: Date.now(),
      });
    }
  }

  async restoreProduct(id: number): Promise<void> {
    await fetcher.patch(
      API_ENDPOINTS.PRESENTATION.RESTORE_PRESENTATION.replace(':id', String(id)),
      {},
      undefined,
      this.consumerPriceUrl
    );
  }

  async upsertProduct(product: CreatePresentationDto & { refId?: string }): Promise<Presentation> {
    const response = await fetcher.put(
      API_ENDPOINTS.PRESENTATION.UPSERT_PRESENTATION,
      product,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as Presentation;
  }

  async getProductCount(
    params: Omit<PresentationQueryParams, 'page' | 'limit'> = {}
  ): Promise<number> {
    const queryParams: Record<string, string> = {};

    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;
    if (params.mainCategory) queryParams.mainCategory = params.mainCategory;
    if (params.category) queryParams.category = params.category;
    if (params.subCategory) queryParams.subCategory = params.subCategory;
    if (params.basketId) queryParams.basketId = params.basketId.toString();
    if (params.search) queryParams.search = params.search;
    if (params.since) queryParams.since = params.since.toString();

    const response = await fetcher.get(
      API_ENDPOINTS.PRESENTATION.GET_PRESENTATION_COUNT,
      queryParams,
      undefined,
      this.analyticsUrl
    );
    return response.data as unknown as number;
  }

  async getLatestPricesWithProducts(
    params: {
      page?: number;
      limit?: number;
      basketId?: number;
      locationId?: number;
      search?: string;
    } = {}
  ): Promise<LatestPricesResponse> {
    const queryParams: Record<string, string> = {};

    if (params.page) queryParams.page = params.page.toString();
    if (params.limit) queryParams.limit = params.limit.toString();
    if (params.basketId) queryParams.basketId = params.basketId.toString();
    if (params.locationId) queryParams.locationId = params.locationId.toString();
    if (params.search) queryParams.search = params.search;

    const response = await fetcher.get(
      API_ENDPOINTS.PRESENTATION.GET_PUBLIC_LATEST_PRICES,
      queryParams,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as unknown as LatestPricesResponse;
  }
}

export const productService = new ProductService();
